import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Mask } from "@/utils/mask";
import { ShowBtn } from "@/components/Show";
import {
  getShowFinancialReconciliationTransaction,
  updateManualReconciliationData,
  getAllReconciliationUuids,
  reconcileCentsDifference,
} from "@/Api/reconcialiationFinancialTransactions";
import { ManualReconciliation } from "./ManualReconciliation";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle, ChevronLeft, ChevronRight, Coins, AlertTriangle } from "lucide-react";

export enum FinancialReconciliationStatus {
  PENDING = 1,
  APPROVED = 2,
  DIVERGENT = 3,
  REJECTED = 4,
  APPROVED_MANUAL = 6,
  APPROVED_AFTER_REVIEW = 7,
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface TableDataProps {
  dataApi: any;
  refetch: () => void;
  pagination?: PaginationMeta | null;
  onPageChange?: (page: number) => void;
  filters?: Record<string, any>;
}

interface SelectedReconciliation {
  uuid: string;
  originalAmount: number;
  reconciledAmount: number;
  editableAmount?: string;
}

export function TableData({ dataApi, refetch, pagination, onPageChange, filters }: TableDataProps) {
  const [selectedItems, setSelectedItems] = useState<SelectedReconciliation[]>(
    []
  );
  const [bulkReconciliationLoading, setBulkReconciliationLoading] =
    useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [editableAmounts, setEditableAmounts] = useState<{
    [key: string]: string;
  }>({});
  const [sourceDescription, setSourceDescription] = useState<string>(
    "Conciliação manual em lote"
  );
  const [loadingAllUuids, setLoadingAllUuids] = useState(false);
  const [reconcileCentsLoading, setReconcileCentsLoading] = useState(false);
  const [showCentsConfirmModal, setShowCentsConfirmModal] = useState(false);

  const handleReconcileCentsDifference = async () => {
    if (!dataApi?.uuid) return;

    setShowCentsConfirmModal(false);
    setReconcileCentsLoading(true);

    try {
      const { ok, data } = await reconcileCentsDifference(dataApi.uuid);

      if (ok) {
        toast.success(data?.response?.message || "Diferenças de centavos conciliadas com sucesso!");
        refetch();
      } else {
        toast.error(data?.message || "Erro ao conciliar diferenças de centavos");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao conciliar diferenças de centavos");
    } finally {
      setReconcileCentsLoading(false);
    }
  };
  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case FinancialReconciliationStatus.APPROVED:
      case FinancialReconciliationStatus.APPROVED_MANUAL:
        return "text-green-600";
      case FinancialReconciliationStatus.APPROVED_AFTER_REVIEW:
        return "text-teal-600";
      case FinancialReconciliationStatus.PENDING:
        return "text-orange-500";
      case FinancialReconciliationStatus.DIVERGENT:
        return "text-red-500";
      case FinancialReconciliationStatus.REJECTED:
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getRowColor = (statusId: number) => {
    switch (statusId) {
      case FinancialReconciliationStatus.APPROVED:
      case FinancialReconciliationStatus.APPROVED_MANUAL:
        return "bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30";
      case FinancialReconciliationStatus.APPROVED_AFTER_REVIEW:
        return "bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/30";
      case FinancialReconciliationStatus.PENDING:
        return "bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30";
      case FinancialReconciliationStatus.DIVERGENT:
        return "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30";
      case FinancialReconciliationStatus.REJECTED:
        return "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30";
      default:
        return "";
    }
  };

  const totals = dataApi?.totals ? {
    originalAmount: dataApi.totals.original_amount,
    reconciledAmount: dataApi.totals.reconciled_amount,
    feeAmount: dataApi.totals.fee_amount,
    netAmount: dataApi.totals.liquid_amount,
    totalDifference: dataApi.totals.total_difference,
    centsDifference: dataApi.totals.cents_difference,
    centsDifferenceCount: dataApi.totals.cents_difference_count,
    divergentCount: dataApi.totals.divergent_count,
  } : null;

  // Calcular totais dos itens selecionados
  const selectedTotals = selectedItems.reduce(
    (acc, item) => {
      const editedAmount = editableAmounts[item.uuid]
        ? Number(Mask.transformCurrencyInFloat(editableAmounts[item.uuid]))
        : item.originalAmount;
      return {
        originalAmount: acc.originalAmount + item.originalAmount,
        reconciledAmount: acc.reconciledAmount + editedAmount,
      };
    },
    { originalAmount: 0, reconciledAmount: 0 }
  );

  const handleSelectItem = (reconciliation: any, checked: boolean) => {
    if (checked) {
      const newItem: SelectedReconciliation = {
        uuid: reconciliation.uuid,
        originalAmount: Number(reconciliation.financial_transaction.amount),
        reconciledAmount: Number(reconciliation.reconciled_amount || 0),
      };
      setSelectedItems((prev) => [...prev, newItem]);
    } else {
      setSelectedItems((prev) =>
        prev.filter((item) => item.uuid !== reconciliation.uuid)
      );
    }
  };

  const handleSelectAllPage = (checked: boolean) => {
    if (checked && dataApi?.financial_reconciliations?.length) {
      const pageItems: SelectedReconciliation[] =
        dataApi.financial_reconciliations.map((reconciliation: any) => ({
          uuid: reconciliation.uuid,
          originalAmount: Number(reconciliation.financial_transaction.amount),
          reconciledAmount: Number(reconciliation.reconciled_amount || 0),
        }));
      setSelectedItems(pageItems);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectAllRecords = async (checked: boolean) => {
    if (!checked) {
      setSelectedItems([]);
      return;
    }

    if (!dataApi?.uuid) return;

    setLoadingAllUuids(true);
    try {
      const params: Record<string, string> = {};
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params[key] = value.toString();
          }
        });
      }

      const { data } = await getAllReconciliationUuids(dataApi.uuid, params);
      const allItems: SelectedReconciliation[] = (data?.response || []).map(
        (item: { uuid: string; amount: number }) => ({
          uuid: item.uuid,
          originalAmount: item.amount,
          reconciledAmount: 0,
        })
      );
      setSelectedItems(allItems);
    } catch (error) {
      console.error("Erro ao buscar todos os UUIDs:", error);
      toast.error("Erro ao selecionar todos os registros");
    } finally {
      setLoadingAllUuids(false);
    }
  };

  const openConfirmationModal = () => {
    if (selectedItems.length === 0) {
      toast.error("Selecione pelo menos um item para conciliar.");
      return;
    }

    // Inicializar valores editáveis com os valores originais
    const initialAmounts: { [key: string]: string } = {};
    selectedItems.forEach((item) => {
      if (!editableAmounts[item.uuid]) {
        initialAmounts[item.uuid] = Mask.formatCurrency(item.originalAmount);
      }
    });
    setEditableAmounts((prev) => ({ ...prev, ...initialAmounts }));

    setShowConfirmationModal(true);
  };

  const handleBulkReconciliation = async () => {
    setShowConfirmationModal(false);
    setBulkReconciliationLoading(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const item of selectedItems) {
        try {
          const editedAmount = editableAmounts[item.uuid]
            ? Number(Mask.transformCurrencyInFloat(editableAmounts[item.uuid]))
            : item.originalAmount;

          const { ok } = await updateManualReconciliationData(item.uuid, {
            reconciled_amount: editedAmount,
            source_description: sourceDescription.trim() || "Conciliação manual em lote",
            reconciliation_status_id:
              FinancialReconciliationStatus.APPROVED_MANUAL,
          });

          if (ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} transação(ões) conciliada(s) com sucesso!`
        );
        setSelectedItems([]);
        setEditableAmounts({}); // Limpar valores editáveis
        setSourceDescription("Conciliação manual em lote"); // Resetar descrição
        refetch();
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} transação(ões) falharam na conciliação.`);
      }
    } catch (error) {
      toast.error("Erro ao processar conciliação em lote.");
    } finally {
      setBulkReconciliationLoading(false);
    }
  };

  const handleAmountChange = (uuid: string, value: string) => {
    setEditableAmounts((prev) => ({
      ...prev,
      [uuid]: value,
    }));
  };

  const selectedUuids = new Set(selectedItems.map((item) => item.uuid));

  const isSelected = (uuid: string) => selectedUuids.has(uuid);

  const isPageSelected =
    dataApi?.financial_reconciliations?.length > 0 &&
    dataApi.financial_reconciliations.every((r: any) => selectedUuids.has(r.uuid));

  const isAllRecordsSelected =
    pagination ? selectedItems.length === pagination.total : false;

  return (
    <>
      {/* Card de Info + Seleção + Paginação */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 mb-1">
        {/* Info do Processo */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-4 border-b">
          <div className="flex items-center gap-4">
            <h2
              className={cn(
                "text-sm font-medium px-3 py-1 border-2 rounded-lg",
                !dataApi?.financial_transaction_type?.id ||
                dataApi?.financial_transaction_type?.id === 1
                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-600"
                  : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-600"
              )}
            >
              {dataApi?.financial_transaction_type?.name || "Geral"}
            </h2>
            <div>
              <h3 className="text-base font-medium">
                {dataApi?.name}
              </h3>
              <div className="flex gap-2 text-sm text-muted-foreground">
                <span>
                  {Mask.transformMaskBirthdayInBr(dataApi?.start_date)} - {Mask.transformMaskBirthdayInBr(dataApi?.end_date)}
                </span>
              </div>
            </div>
          </div>

          {/* Paginação */}
          {pagination && pagination.last_page > 1 && onPageChange && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">
                Página {pagination.current_page} de {pagination.last_page}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(pagination.current_page - 1)}
                disabled={pagination.current_page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.last_page <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.current_page >= pagination.last_page - 2) {
                    pageNum = pagination.last_page - 4 + i;
                  } else {
                    pageNum = pagination.current_page - 2 + i;
                  }
                  const isCurrentPage = Number(pagination.current_page) === pageNum;
                  return (
                    <Button
                      key={pageNum}
                      variant="outline"
                      size="sm"
                      className={cn(
                        "w-9",
                        isCurrentPage && "bg-blue-600 hover:bg-blue-700 border-blue-600"
                      )}
                      onClick={() => onPageChange(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(pagination.current_page + 1)}
                disabled={pagination.current_page >= pagination.last_page}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Seleção e Ações em Lote */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pt-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-page"
                  checked={isPageSelected}
                  onCheckedChange={handleSelectAllPage}
                />
                <label
                  htmlFor="select-page"
                  className="text-sm font-medium cursor-pointer"
                >
                  Selecionar página atual (
                  {dataApi?.financial_reconciliations?.length || 0})
                </label>
              </div>

              {pagination && pagination.total > (pagination.per_page || 100) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all-records"
                    checked={isAllRecordsSelected}
                    onCheckedChange={handleSelectAllRecords}
                    disabled={loadingAllUuids}
                  />
                  <label
                    htmlFor="select-all-records"
                    className="text-sm font-medium cursor-pointer"
                  >
                    {loadingAllUuids
                      ? "Carregando..."
                      : `Selecionar todos (${pagination.total})`}
                  </label>
                </div>
              )}
            </div>

            {selectedItems.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {selectedItems.length} item(ns) selecionado(s)
                {pagination && selectedItems.length === pagination.total && " (todos)"}
              </div>
            )}
          </div>

          {selectedItems.length > 0 && (
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-muted-foreground">Valor Original</div>
                  <div className="font-medium text-green-600">
                    {Mask.formatCurrency(selectedTotals.originalAmount)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-muted-foreground">Valor Conciliado</div>
                  <div className="font-medium text-blue-600">
                    {Mask.formatCurrency(selectedTotals.reconciledAmount)}
                  </div>
                </div>
              </div>

              <Button
                onClick={openConfirmationModal}
                disabled={bulkReconciliationLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {bulkReconciliationLoading
                  ? "Processando..."
                  : "Conciliar Selecionados"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Card de Diferenças de Centavos - Já confirmado */}
      {dataApi?.cents_difference_reconciled !== null && dataApi?.cents_difference_reconciled !== undefined && (
        <div className="bg-gradient-to-r from-amber-50 to-green-50 dark:from-amber-900/20 dark:to-green-900/20 border-2 border-amber-300 dark:border-amber-600 rounded-lg p-4 mb-2 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-800">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-800 dark:text-amber-200 font-semibold text-base">
                    Sobras de Centavos Confirmadas
                  </span>
                  <span className="px-2 py-0.5 text-xs font-bold rounded bg-green-600 text-white">
                    CONCILIADO
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {dataApi?.cents_difference_reconciled_by?.name && (
                    <span>
                      Confirmado por <strong>{dataApi.cents_difference_reconciled_by.name}</strong>
                    </span>
                  )}
                  {dataApi?.cents_difference_reconciled_at && (
                    <span>
                      em {new Date(dataApi.cents_difference_reconciled_at).toLocaleDateString("pt-BR")} às {new Date(dataApi.cents_difference_reconciled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm text-gray-500 dark:text-gray-400">Valor total das sobras:</span>
              <div className="text-xl font-bold text-amber-700 dark:text-amber-300">
                {Mask.formatCurrency(Math.abs(dataApi.cents_difference_reconciled))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card de Diferenças de Centavos - Pendente */}
      {(dataApi?.cents_difference_reconciled === null || dataApi?.cents_difference_reconciled === undefined) && totals && (totals.centsDifferenceCount > 0 || totals.divergentCount > 0) && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              {totals.centsDifferenceCount > 0 && (
                <div className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-700 dark:text-amber-300 font-medium text-sm">
                    Sobras de centavos ({totals.centsDifferenceCount} itens):
                  </span>
                  <span className="text-amber-800 dark:text-amber-200 font-bold">
                    {Mask.formatCurrency(totals.centsDifference)}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-200 text-amber-800 dark:bg-amber-700 dark:text-amber-200">
                    PENDENTE
                  </span>
                </div>
              )}
              {totals.divergentCount > 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium text-sm">
                    {totals.divergentCount} divergência(s) pendente(s)
                  </span>
                </div>
              )}
            </div>
            {totals.centsDifferenceCount > 0 && (
              <Button
                size="sm"
                variant={totals.divergentCount > 0 ? "outline" : "default"}
                disabled={totals.divergentCount > 0 || reconcileCentsLoading}
                onClick={() => setShowCentsConfirmModal(true)}
                className={cn(
                  "gap-2",
                  totals.divergentCount === 0 && "bg-green-600 hover:bg-green-700"
                )}
                title={totals.divergentCount > 0
                  ? "Ajuste as divergências manualmente antes de confirmar as sobras"
                  : "Confirmar sobras de centavos"
                }
              >
                <CheckCircle className="h-4 w-4" />
                {reconcileCentsLoading ? "Processando..." : "Confirmar Sobras"}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="rounded-md border overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto relative">
          <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
            <TableHeader className="[&_tr]:border-b">
              <TableRow className="bg-white dark:bg-[#2c2c2c] border-b">
                <TableHead className="w-[50px] sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">
                  <Checkbox
                    checked={isPageSelected}
                    onCheckedChange={handleSelectAllPage}
                  />
                </TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Descrição</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Valor Original</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Valor Conciliado</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Valor de Taxa</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Valor Líquido</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Código de Transação</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Forma de Pagamento</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Status</TableHead>
                <TableHead className="sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Data Conciliação</TableHead>
                <TableHead className="w-[50px] sticky top-0 bg-white dark:bg-[#2c2c2c] z-10 shadow-[0_1px_0_0_#e5e7eb] dark:shadow-[0_1px_0_0_#3f3f46]">Ações</TableHead>
              </TableRow>
            </TableHeader>
          <TableBody>
            {dataApi?.financial_reconciliations?.length > 0 ? (
              dataApi?.financial_reconciliations?.map((reconciliation) => (
                <TableRow
                  key={reconciliation.id}
                  className={getRowColor(
                    reconciliation?.reconciliation_status?.id
                  )}
                >
                  <TableCell>
                    <Checkbox
                      checked={isSelected(reconciliation.uuid)}
                      onCheckedChange={(checked) =>
                        handleSelectItem(reconciliation, checked as boolean)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/contas-a-pagar?${new URLSearchParams({
                        description: reconciliation.financial_transaction.description || "",
                        type: String(
                          reconciliation.financial_transaction.financial_transaction_type_id ?? ""
                        ),
                        ...(reconciliation.financial_transaction.payment_date
                          ? {
                              payment_date: reconciliation.financial_transaction.payment_date.slice(0, 10),
                            }
                          : {}),
                      }).toString()}`}
                      target="_blank"
                      title="Abrir em Transações Financeiras"
                      className="hover:underline text-blue-700 dark:text-blue-400"
                    >
                      {reconciliation.financial_transaction.description}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {Mask.formatCurrency(
                      reconciliation.financial_transaction.amount
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">
                        {reconciliation.reconciled_at
                          ? Mask.formatCurrency(
                              reconciliation.reconciled_amount
                            )
                          : "Não conciliado"}
                      </span>
                      {reconciliation.reconciliation_status.id ===
                        FinancialReconciliationStatus.DIVERGENT && (
                        <div className="mt-1 p-2 bg-red-100 dark:bg-red-900/30 rounded-md">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-red-700 dark:text-red-300">
                              Valor Original:
                            </span>
                            <span className="font-medium text-red-700 dark:text-red-300">
                              {Mask.formatCurrency(
                                reconciliation.financial_transaction.amount
                              )}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-red-200 dark:border-red-700">
                            <span className="text-red-700 dark:text-red-300">
                              Diferença:
                            </span>
                            <span className="font-medium text-red-700 dark:text-red-300">
                              {Mask.formatCurrency(
                                Math.abs(
                                  Number(
                                    reconciliation.financial_transaction.amount
                                  ) - reconciliation.reconciled_amount
                                )
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {Mask.formatCurrency(reconciliation?.fee_amount || 0)}
                  </TableCell>
                  <TableCell>
                    {Mask.formatCurrency(reconciliation?.liquid_amount || 0)}
                  </TableCell>
                  <TableCell>
                    {reconciliation?.financial_transaction
                      ?.sale_code_transaction || "-"}
                  </TableCell>
                  <TableCell>{reconciliation?.payment_form?.name}</TableCell>
                  <TableCell
                    className={getStatusColor(
                      reconciliation?.reconciliation_status?.id
                    )}
                  >
                    {reconciliation?.reconciliation_status?.name}
                  </TableCell>
                  <TableCell>
                    {reconciliation.reconciled_at
                      ? Mask.transformMaskBirthdayInBr(
                          reconciliation.reconciled_at
                        )
                      : "Não conciliado"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-3 w-full justify-center">
                      <ManualReconciliation
                        uuid={reconciliation.uuid}
                        sourceDescription={
                          reconciliation?.source_description || ""
                        }
                        originalAmount={
                          reconciliation.financial_transaction.amount
                        }
                        onSuccess={refetch}
                      />
                      <ShowBtn
                        params={{
                          func: getShowFinancialReconciliationTransaction,
                          funcParams: dataApi?.uuid,
                        }}
                        className="sm:max-w-[700px]"
                        render={() => (
                          <div className="flex flex-col gap-4 max-h-[600px] overflow-y-auto p-1">
                            {/* Card: Conciliação */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-750 rounded-xl border border-blue-100 dark:border-gray-700 p-5">
                              <div className="flex items-center gap-2 mb-4">
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                <h4 className="font-semibold text-base text-gray-800 dark:text-gray-200">
                                  Conciliação
                                </h4>
                              </div>
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div className="col-span-3 bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Descrição
                                  </span>
                                  <p className="text-gray-800 dark:text-gray-200 mt-1 font-medium">
                                    {reconciliation?.financial_transaction?.description || "Não informado"}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Status
                                  </span>
                                  <p className={cn(
                                    "mt-1 font-semibold",
                                    reconciliation?.reconciliation_status?.id === FinancialReconciliationStatus.APPROVED && "text-green-600",
                                    reconciliation?.reconciliation_status?.id === FinancialReconciliationStatus.APPROVED_MANUAL && "text-green-600",
                                    reconciliation?.reconciliation_status?.id === FinancialReconciliationStatus.APPROVED_AFTER_REVIEW && "text-teal-600",
                                    reconciliation?.reconciliation_status?.id === FinancialReconciliationStatus.PENDING && "text-orange-500",
                                    reconciliation?.reconciliation_status?.id === FinancialReconciliationStatus.DIVERGENT && "text-red-500"
                                  )}>
                                    {reconciliation?.reconciliation_status?.name}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Conciliado por
                                  </span>
                                  <p className="text-gray-800 dark:text-gray-200 mt-1">
                                    {reconciliation?.reconciled_by?.name || "—"}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Data Conciliação
                                  </span>
                                  <p className="text-gray-800 dark:text-gray-200 mt-1">
                                    {reconciliation?.reconciled_at
                                      ? new Date(reconciliation.reconciled_at).toLocaleString("pt-BR")
                                      : "—"}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Forma de Pagamento
                                  </span>
                                  <p className="text-gray-800 dark:text-gray-200 mt-1">
                                    {reconciliation?.payment_form?.name}
                                  </p>
                                </div>
                                {reconciliation?.source_description && (
                                  <div className="col-span-2 bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      Descrição da Conciliação
                                    </span>
                                    <p className="text-gray-800 dark:text-gray-200 mt-1">
                                      {reconciliation.source_description}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Card: Venda */}
                            {reconciliation?.financial_transaction?.sale && (
                              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-750 rounded-xl border border-green-100 dark:border-gray-700 p-5">
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <h4 className="font-semibold text-base text-gray-800 dark:text-gray-200">
                                      Venda
                                    </h4>
                                  </div>
                                  <a
                                    href={`/vendas?sale_id=${reconciliation.financial_transaction.sale.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                                  >
                                    Ver Venda #{reconciliation.financial_transaction.sale.id}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      Cliente
                                    </span>
                                    <p className="text-gray-800 dark:text-gray-200 mt-1 font-medium">
                                      {reconciliation.financial_transaction.sale.client?.name || "—"}
                                    </p>
                                  </div>
                                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      Vendedor
                                    </span>
                                    <p className="text-gray-800 dark:text-gray-200 mt-1">
                                      {reconciliation.financial_transaction.sale.seller?.user?.name || "—"}
                                    </p>
                                  </div>
                                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      Tipo
                                    </span>
                                    <p className="text-gray-800 dark:text-gray-200 mt-1">
                                      {reconciliation.financial_transaction.sale.sale_type?.name || "—"}
                                    </p>
                                  </div>
                                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                      Status
                                    </span>
                                    <p className="text-gray-800 dark:text-gray-200 mt-1">
                                      {reconciliation.financial_transaction.sale.sale_status?.name || "—"}
                                    </p>
                                  </div>
                                  {reconciliation.financial_transaction.sale_code_transaction && (
                                    <div className="col-span-2 bg-white dark:bg-gray-800 rounded-lg p-3">
                                      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Código de Transação
                                      </span>
                                      <p className="text-gray-800 dark:text-gray-200 mt-1 font-mono text-xs">
                                        {reconciliation.financial_transaction.sale_code_transaction}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Card: Valores */}
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-750 rounded-xl border border-purple-100 dark:border-gray-700 p-5">
                              <div className="flex items-center gap-2 mb-4">
                                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                <h4 className="font-semibold text-base text-gray-800 dark:text-gray-200">
                                  Valores
                                </h4>
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Valor Original
                                  </span>
                                  <p className="text-green-600 dark:text-green-400 mt-1 font-bold text-lg">
                                    {Mask.formatCurrency(reconciliation?.financial_transaction?.amount || 0)}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Valor Conciliado
                                  </span>
                                  <p className="text-blue-600 dark:text-blue-400 mt-1 font-bold text-lg">
                                    {Mask.formatCurrency(reconciliation?.reconciled_amount || 0)}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Taxa
                                  </span>
                                  <p className="text-red-600 dark:text-red-400 mt-1 font-semibold">
                                    {Mask.formatCurrency(reconciliation?.fee_amount || 0)}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Valor Líquido
                                  </span>
                                  <p className="text-emerald-600 dark:text-emerald-400 mt-1 font-semibold">
                                    {Mask.formatCurrency(reconciliation?.liquid_amount || 0)}
                                  </p>
                                </div>
                                {reconciliation?.reconciliation_status?.id === FinancialReconciliationStatus.DIVERGENT && (
                                  <div className="col-span-2 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                                    <span className="text-xs uppercase tracking-wide text-red-600 dark:text-red-400">
                                      Diferença
                                    </span>
                                    <p className="text-red-600 dark:text-red-400 mt-1 font-bold text-lg">
                                      {Mask.formatCurrency(
                                        Math.abs(Number(reconciliation?.financial_transaction?.amount || 0) - Number(reconciliation?.reconciled_amount || 0))
                                      )}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Card: Datas */}
                            <div className="bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-800 dark:to-gray-750 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                              <div className="flex items-center gap-2 mb-4">
                                <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                                <h4 className="font-semibold text-base text-gray-800 dark:text-gray-200">
                                  Datas
                                </h4>
                              </div>
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Criação
                                  </span>
                                  <p className="text-gray-800 dark:text-gray-200 mt-1">
                                    {reconciliation?.created_at
                                      ? new Date(reconciliation.created_at).toLocaleString("pt-BR")
                                      : "—"}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Pagamento
                                  </span>
                                  <p className="text-gray-800 dark:text-gray-200 mt-1">
                                    {reconciliation?.financial_transaction?.payment_date
                                      ? Mask.transformMaskBirthdayInBr(
                                          reconciliation.financial_transaction.payment_date.slice(0, 10)
                                        )
                                      : "—"}
                                  </p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Conciliação
                                  </span>
                                  <p className="text-gray-800 dark:text-gray-200 mt-1">
                                    {reconciliation?.reconciled_at
                                      ? new Date(reconciliation.reconciled_at).toLocaleString("pt-BR")
                                      : "—"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        labels={{
                          title: "Detalhes da Conciliação",
                          description:
                            "Informações detalhadas sobre a conciliação",
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} className="h-24 text-center">
                  Nenhuma conciliação encontrada.
                </TableCell>
              </TableRow>
            )}
            {totals && (
              <>
                <TableRow className="font-medium bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700">
                  <TableCell></TableCell>
                  <TableCell className="text-lg">Total Geral</TableCell>
                  <TableCell className="">
                    <span className="text-green-600 hover:text-green-400">
                      {Mask.formatCurrency(totals.originalAmount)}
                    </span>
                  </TableCell>
                  <TableCell className="">
                    <span className="text-green-600 hover:text-green-400">
                      {Mask.formatCurrency(totals.reconciledAmount)}
                    </span>
                  </TableCell>
                  <TableCell className="">
                    <span className="text-red-600 dark:text-red-400">
                      {Mask.formatCurrency(totals.feeAmount)}
                    </span>
                  </TableCell>
                  <TableCell className="">
                    <span className="text-green-600 dark:text-green-400">
                      {Mask.formatCurrency(totals.netAmount)}
                    </span>
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
          </Table>
        </div>
      </div>

      {/* Paginação */}
      {pagination && pagination.last_page > 1 && onPageChange && (
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border rounded-lg mt-4">
          <div className="text-sm text-muted-foreground">
            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{" "}
            {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de{" "}
            {pagination.total} registros
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                let pageNum: number;
                if (pagination.last_page <= 5) {
                  pageNum = i + 1;
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.current_page >= pagination.last_page - 2) {
                  pageNum = pagination.last_page - 4 + i;
                } else {
                  pageNum = pagination.current_page - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={pagination.current_page === pageNum ? "default" : "outline"}
                    size="sm"
                    className="w-9"
                    onClick={() => onPageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.last_page}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <Dialog
        open={showConfirmationModal}
        onOpenChange={setShowConfirmationModal}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Confirmar Conciliação em Lote
            </DialogTitle>
            <DialogDescription>
              Você está prestes a conciliar manualmente os itens selecionados.
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Resumo da Conciliação */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Resumo da Conciliação
              </h4>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    Itens selecionados:
                  </span>
                  <div className="font-medium text-blue-600">
                    {selectedItems.length}
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground">Status final:</span>
                  <div className="font-medium text-green-600">
                    Aprovado Manualmente
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground">
                    Valor Original Total:
                  </span>
                  <div className="font-medium text-green-600">
                    {Mask.formatCurrency(selectedTotals.originalAmount)}
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground">
                    Valor Total a Conciliar:
                  </span>
                  <div className="font-medium text-blue-600">
                    {Mask.formatCurrency(selectedTotals.reconciledAmount)}
                  </div>
                </div>
              </div>

            </div>

            {/* Campo de Descrição Geral */}
            <div className="space-y-2">
              <Label htmlFor="source-description" className="text-sm font-medium">
                Descrição da Fonte (Aplicada a todos os itens)
              </Label>
              <Textarea
                id="source-description"
                value={sourceDescription}
                onChange={(e) => setSourceDescription(e.target.value)}
                placeholder="Ex: Conciliação manual em lote - Ajuste de divergências"
                className="min-h-[80px] resize-none"
                maxLength={500}
              />
              <div className="text-xs text-muted-foreground">
                Esta descrição será aplicada a todas as {selectedItems.length} transações
                selecionadas. Você pode editar os valores individuais abaixo.
              </div>
            </div>

            {/* Lista dos itens selecionados com valores editáveis */}
            <div className="max-h-60 overflow-y-auto">
              <h5 className="font-medium text-sm mb-3">
                Itens que serão conciliados:
              </h5>
              <div className="space-y-3">
                {selectedItems.map((item, index) => {
                  const reconciliation =
                    dataApi?.financial_reconciliations?.find(
                      (r: any) => r.uuid === item.uuid
                    );
                  return (
                    <div
                      key={item.uuid}
                      className="bg-white dark:bg-gray-700 rounded-lg p-3 border"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                            {index + 1}.{" "}
                            {reconciliation?.financial_transaction
                              ?.description || "Sem descrição"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Valor original:{" "}
                            {Mask.formatCurrency(item.originalAmount)}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1">
                          <label className="text-xs text-muted-foreground">
                            Valor a conciliar:
                          </label>
                          <Input
                            typeMask="currency"
                            value={
                              editableAmounts[item.uuid] ||
                              Mask.formatCurrency(item.originalAmount)
                            }
                            onChange={(e) =>
                              handleAmountChange(item.uuid, e.target.value)
                            }
                            className="w-32 h-8 text-right text-sm"
                            placeholder="R$ 0,00"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmationModal(false);
                // Manter os valores editados caso o usuário abra o modal novamente
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleBulkReconciliation}
              disabled={bulkReconciliationLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {bulkReconciliationLoading
                ? "Processando..."
                : "Confirmar Conciliação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação - Sobras de Centavos */}
      <Dialog
        open={showCentsConfirmModal}
        onOpenChange={setShowCentsConfirmModal}
      >
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-600" />
              Confirmar Sobras de Centavos
            </DialogTitle>
            <DialogDescription>
              Esta ação registra a confirmação de que as diferenças de centavos
              (até R$ 0,99) foram reconhecidas pelo responsável.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Itens com sobra:</span>
                  <div className="font-bold text-amber-700 dark:text-amber-300 text-lg">
                    {totals?.centsDifferenceCount || 0}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Total das sobras:</span>
                  <div className="font-bold text-amber-700 dark:text-amber-300 text-lg">
                    {Mask.formatCurrency(totals?.centsDifference || 0)}
                  </div>
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <p>
                Ao confirmar, você atesta que as diferenças de centavos foram verificadas
                e estão de acordo. O valor total das sobras ficará registrado com seu nome
                e data da confirmação.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCentsConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReconcileCentsDifference}
              disabled={reconcileCentsLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {reconcileCentsLoading ? "Processando..." : "Confirmar Sobras"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
